import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Icons
import { Shield, AlertTriangle, FileText, Users, Plus, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SchoolLifeManagement = ({ user }) => {
  const [incidents, setIncidents] = useState([]);
  const [sanctions, setSanctions] = useState([]);
  const [behaviorReports, setBehaviorReports] = useState([]);
  const [students, setStudents] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incidents');
  
  // Dialog states
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [sanctionDialogOpen, setSanctionDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  
  const [incidentForm, setIncidentForm] = useState({
    student_id: '',
    incident_type: '',
    title: '',
    description: '',
    severity: '',
    location: '',
    witness_ids: [],
    incident_date: new Date().toISOString().split('T')[0]
  });

  const [sanctionForm, setSanctionForm] = useState({
    incident_id: '',
    student_id: '',
    sanction_type: '',
    title: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  const [reportForm, setReportForm] = useState({
    student_id: '',
    period: '',
    behavior_score: 10,
    attitude_score: 10,
    participation_score: 10,
    comments: '',
    recommendations: ''
  });

  const incidentTypes = [
    { value: 'BEHAVIOR', label: 'Problème de comportement', color: 'badge-warning' },
    { value: 'VIOLENCE', label: 'Violence', color: 'badge-danger' },
    { value: 'DISRUPTION', label: 'Perturbation de cours', color: 'badge-warning' },
    { value: 'ABSENCE', label: 'Absence non justifiée', color: 'badge-secondary' },
    { value: 'LATE', label: 'Retards répétés', color: 'badge-warning' },
    { value: 'HOMEWORK', label: 'Devoirs non faits', color: 'badge-secondary' },
    { value: 'MATERIAL', label: 'Matériel oublié', color: 'badge-secondary' },
    { value: 'RESPECT', label: 'Manque de respect', color: 'badge-danger' }
  ];

  const severityLevels = [
    { value: 'LOW', label: 'Faible', color: 'text-green-600' },
    { value: 'MEDIUM', label: 'Moyen', color: 'text-orange-600' },
    { value: 'HIGH', label: 'Élevé', color: 'text-red-600' },
    { value: 'CRITICAL', label: 'Critique', color: 'text-red-800' }
  ];

  const sanctionTypes = [
    { value: 'WARNING', label: 'Avertissement' },
    { value: 'DETENTION', label: 'Retenue' },
    { value: 'EXCLUSION_TEMPORARY', label: 'Exclusion temporaire' },
    { value: 'EXCLUSION_PERMANENT', label: 'Exclusion définitive' },
    { value: 'COMMUNITY_SERVICE', label: 'Travaux d\'intérêt général' },
    { value: 'PARENT_MEETING', label: 'Convocation des parents' },
    { value: 'REPORT_CARD', label: 'Inscription au dossier' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const promises = [
        axios.get(`${API}/incidents`),
        axios.get(`${API}/sanctions`),
        axios.get(`${API}/behavior-reports`),
        axios.get(`${API}/students`),
        axios.get(`${API}/discipline/statistics`)
      ];

      const [incidentsRes, sanctionsRes, reportsRes, studentsRes, statsRes] = await Promise.all(promises);

      setIncidents(incidentsRes.data);
      setSanctions(sanctionsRes.data);
      setBehaviorReports(reportsRes.data);
      setStudents(studentsRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIncidentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/incidents`, {
        ...incidentForm,
        incident_date: new Date(incidentForm.incident_date).toISOString()
      });

      setIncidents(prev => [response.data, ...prev]);
      toast.success('Incident signalé avec succès');
      resetIncidentForm();
      setIncidentDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors du signalement');
      console.error('Error creating incident:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSanctionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/sanctions`, {
        ...sanctionForm,
        start_date: new Date(sanctionForm.start_date).toISOString(),
        end_date: sanctionForm.end_date ? new Date(sanctionForm.end_date).toISOString() : null
      });

      setSanctions(prev => [response.data, ...prev]);
      toast.success('Sanction appliquée avec succès');
      resetSanctionForm();
      setSanctionDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de l\'application de la sanction');
      console.error('Error creating sanction:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/behavior-reports`, reportForm);

      setBehaviorReports(prev => [response.data, ...prev]);
      toast.success('Rapport comportemental enregistré');
      resetReportForm();
      setReportDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement du rapport');
      console.error('Error creating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetIncidentForm = () => {
    setIncidentForm({
      student_id: '',
      incident_type: '',
      title: '',
      description: '',
      severity: '',
      location: '',
      witness_ids: [],
      incident_date: new Date().toISOString().split('T')[0]
    });
  };

  const resetSanctionForm = () => {
    setSanctionForm({
      incident_id: '',
      student_id: '',
      sanction_type: '',
      title: '',
      description: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: ''
    });
  };

  const resetReportForm = () => {
    setReportForm({
      student_id: '',
      period: '',
      behavior_score: 10,
      attitude_score: 10,
      participation_score: 10,
      comments: '',
      recommendations: ''
    });
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Élève inconnu';
  };

  const getIncidentTypeInfo = (type) => {
    return incidentTypes.find(t => t.value === type) || incidentTypes[0];
  };

  const getSeverityInfo = (severity) => {
    return severityLevels.find(s => s.value === severity) || severityLevels[0];
  };

  const getSanctionTypeLabel = (type) => {
    const sanctionType = sanctionTypes.find(s => s.value === type);
    return sanctionType ? sanctionType.label : type;
  };

  const canManageSchoolLife = ['TEACHER', 'LIFE_COUNSELOR', 'ADMIN', 'PRINCIPAL'].includes(user.role);

  if (loading && incidents.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="school-life-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Shield className="w-6 h-6 mr-2 text-blue-600" />
            Vie Scolaire & Discipline
          </h1>
          <p className="text-gray-600 mt-1">
            Gestion des incidents, sanctions et suivi comportemental
          </p>
        </div>

        {canManageSchoolLife && (
          <div className="flex space-x-3">
            <Dialog open={incidentDialogOpen} onOpenChange={setIncidentDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="report-incident-btn"
                  onClick={() => { resetIncidentForm(); setIncidentDialogOpen(true); }}
                  className="btn-danger"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Signaler Incident
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Signaler un incident</DialogTitle>
                  <DialogDescription>
                    Enregistrez un incident disciplinaire
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleIncidentSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="student">Élève concerné</Label>
                    <Select 
                      value={incidentForm.student_id} 
                      onValueChange={(value) => setIncidentForm({ ...incidentForm, student_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un élève" />
                      </SelectTrigger>
                      <SelectContent>
                        {students.map((student) => (
                          <SelectItem key={student.id} value={student.id}>
                            {student.first_name} {student.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="incident_type">Type d'incident</Label>
                      <Select 
                        value={incidentForm.incident_type} 
                        onValueChange={(value) => setIncidentForm({ ...incidentForm, incident_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {incidentTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="severity">Gravité</Label>
                      <Select 
                        value={incidentForm.severity} 
                        onValueChange={(value) => setIncidentForm({ ...incidentForm, severity: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Gravité" />
                        </SelectTrigger>
                        <SelectContent>
                          {severityLevels.map((level) => (
                            <SelectItem key={level.value} value={level.value}>
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="title">Titre de l'incident</Label>
                    <Input
                      id="title"
                      value={incidentForm.title}
                      onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })}
                      placeholder="Résumé de l'incident"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description détaillée</Label>
                    <Textarea
                      id="description"
                      value={incidentForm.description}
                      onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
                      placeholder="Décrivez l'incident en détail..."
                      rows={4}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="location">Lieu</Label>
                      <Input
                        id="location"
                        value={incidentForm.location}
                        onChange={(e) => setIncidentForm({ ...incidentForm, location: e.target.value })}
                        placeholder="Salle, cour, couloir..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="incident_date">Date</Label>
                      <Input
                        id="incident_date"
                        type="date"
                        value={incidentForm.incident_date}
                        onChange={(e) => setIncidentForm({ ...incidentForm, incident_date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIncidentDialogOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="btn-danger"
                    >
                      {loading ? 'Signalement...' : 'Signaler l\'incident'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {(user.role === 'LIFE_COUNSELOR' || user.role === 'ADMIN' || user.role === 'PRINCIPAL') && (
              <Dialog open={sanctionDialogOpen} onOpenChange={setSanctionDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={() => { resetSanctionForm(); setSanctionDialogOpen(true); }}
                    className="btn-warning"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Appliquer Sanction
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Appliquer une sanction</DialogTitle>
                    <DialogDescription>
                      Attribuez une sanction disciplinaire
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={handleSanctionSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sanction_student">Élève</Label>
                      <Select 
                        value={sanctionForm.student_id} 
                        onValueChange={(value) => setSanctionForm({ ...sanctionForm, student_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez un élève" />
                        </SelectTrigger>
                        <SelectContent>
                          {students.map((student) => (
                            <SelectItem key={student.id} value={student.id}>
                              {student.first_name} {student.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="incident">Incident lié (optionnel)</Label>
                      <Select 
                        value={sanctionForm.incident_id} 
                        onValueChange={(value) => setSanctionForm({ ...sanctionForm, incident_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir un incident" />
                        </SelectTrigger>
                        <SelectContent>
                          {incidents.map((incident) => (
                            <SelectItem key={incident.id} value={incident.id}>
                              {incident.title} - {getStudentName(incident.student_id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sanction_type">Type de sanction</Label>
                      <Select 
                        value={sanctionForm.sanction_type} 
                        onValueChange={(value) => setSanctionForm({ ...sanctionForm, sanction_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Type de sanction" />
                        </SelectTrigger>
                        <SelectContent>
                          {sanctionTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sanction_title">Titre</Label>
                      <Input
                        id="sanction_title"
                        value={sanctionForm.title}
                        onChange={(e) => setSanctionForm({ ...sanctionForm, title: e.target.value })}
                        placeholder="Titre de la sanction"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sanction_description">Description</Label>
                      <Textarea
                        id="sanction_description"
                        value={sanctionForm.description}
                        onChange={(e) => setSanctionForm({ ...sanctionForm, description: e.target.value })}
                        placeholder="Détails de la sanction..."
                        rows={3}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start_date">Date de début</Label>
                        <Input
                          id="start_date"
                          type="date"
                          value={sanctionForm.start_date}
                          onChange={(e) => setSanctionForm({ ...sanctionForm, start_date: e.target.value })}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="end_date">Date de fin (optionnelle)</Label>
                        <Input
                          id="end_date"
                          type="date"
                          value={sanctionForm.end_date}
                          onChange={(e) => setSanctionForm({ ...sanctionForm, end_date: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setSanctionDialogOpen(false)}
                      >
                        Annuler
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="btn-warning"
                      >
                        {loading ? 'Application...' : 'Appliquer la sanction'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}
      </div>

      {/* Statistics */}
      {Object.keys(statistics).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Incidents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{statistics.total_incidents}</div>
              <p className="text-xs text-gray-600 mt-1">Incidents signalés</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">En Cours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{statistics.open_incidents}</div>
              <p className="text-xs text-gray-600 mt-1">Incidents ouverts</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Sanctions Actives</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{statistics.active_sanctions}</div>
              <p className="text-xs text-gray-600 mt-1">Sanctions en cours</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Résolus</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{statistics.resolved_incidents}</div>
              <p className="text-xs text-gray-600 mt-1">Incidents résolus</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="sanctions">Sanctions</TabsTrigger>
          <TabsTrigger value="reports">Rapports Comportementaux</TabsTrigger>
        </TabsList>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          {incidents.length > 0 ? (
            <div className="space-y-4">
              {incidents.map((incident) => {
                const typeInfo = getIncidentTypeInfo(incident.incident_type);
                const severityInfo = getSeverityInfo(incident.severity);

                return (
                  <Card key={incident.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold">{incident.title}</h3>
                            <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                            <Badge className={`badge-${incident.severity === 'CRITICAL' ? 'danger' : incident.severity === 'HIGH' ? 'warning' : 'secondary'}`}>
                              {severityInfo.label}
                            </Badge>
                            <Badge className={`badge-${incident.status === 'RESOLVED' ? 'success' : 'warning'}`}>
                              {incident.status}
                            </Badge>
                          </div>

                          <p className="text-sm text-gray-600 mb-2">
                            Élève: {getStudentName(incident.student_id)}
                          </p>

                          <p className="text-sm text-gray-700 mb-3">
                            {incident.description}
                          </p>

                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>
                              Date: {new Date(incident.incident_date).toLocaleDateString('fr-FR')}
                            </span>
                            {incident.location && <span>Lieu: {incident.location}</span>}
                            <span>
                              Signalé le {new Date(incident.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertTriangle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun incident</h3>
              <p className="text-gray-600">
                Aucun incident n'a été signalé.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Sanctions Tab */}
        <TabsContent value="sanctions" className="space-y-4">
          {sanctions.length > 0 ? (
            <div className="space-y-4">
              {sanctions.map((sanction) => (
                <Card key={sanction.id} className="card">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-semibold">{sanction.title}</h3>
                          <Badge className={`badge-${sanction.status === 'COMPLETED' ? 'success' : sanction.status === 'ACTIVE' ? 'warning' : 'secondary'}`}>
                            {sanction.status}
                          </Badge>
                        </div>

                        <p className="text-sm text-gray-600 mb-2">
                          Élève: {getStudentName(sanction.student_id)}
                        </p>

                        <p className="text-sm text-gray-600 mb-2">
                          Type: {getSanctionTypeLabel(sanction.sanction_type)}
                        </p>

                        <p className="text-sm text-gray-700 mb-3">
                          {sanction.description}
                        </p>

                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            Début: {new Date(sanction.start_date).toLocaleDateString('fr-FR')}
                          </span>
                          {sanction.end_date && (
                            <span>
                              Fin: {new Date(sanction.end_date).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune sanction</h3>
              <p className="text-gray-600">
                Aucune sanction n'a été appliquée.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Rapports Comportementaux</h3>
            <p className="text-gray-600 mb-6">
              Fonctionnalité en cours de développement
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolLifeManagement;